

















































































































































































import { namespace } from "vuex-class";
import { Component, Vue, Prop } from "vue-property-decorator";
import rest from "@/rest";
import { l3draggable, l3droppable } from "@/directives/l3dragDrop";
// components
import MarkdownViewer from "../Shared/MarkdownViewer.vue";
import MarkdownEditor from "../Shared/MarkdownEditor.vue";
import ImageWithDnD from "../Shared/ImageWithDnD.vue";
// interfaces
import { ExerciseVM } from "@/interfaces/ExerciseVM";
import { CourseElementVm, CustomTranslationType, QuizOptionVm } from "@/interfaces/CourseElementVm";
import { CustomizationVm } from "@/interfaces/CustomizationVm";
import { CcPageVm, InteractiveListAiSuggestionVm } from "@/interfaces/InteractiveListAiSuggestionVm";

const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    MarkdownViewer,
    MarkdownEditor,
    ImageWithDnD
  },
  directives: { l3draggable, l3droppable }
})
export default class SequentialListElement extends Vue {
  @Prop() element!: CourseElementVm;
  @customization.Getter customizationSettings!: CustomizationVm;
  @globals.Getter selectedExercise!: ExerciseVM;
  @globals.Getter selectedModulePage!: CcPageVm | null;
  @globals.Getter isMasterLanguageCode!: boolean;

  isBusy = false;
  showMarkdownEditor = false;
  selectedOption: QuizOptionVm | null = null;

  // AI dialog state
  showAiDialog: boolean = false;
  aiPrompt: string = "";

  onAddOption() {
    // Ensure the options array exists on the element
    if (!this.element.options) {
      (this.element as any).options = [];
    }

    // Add a new option with sensible defaults
    const newOption: QuizOptionVm = {
      id: this.element.options!.length + 1,
      guid: this.$globalHelper.getNewGuid(),
      customTranslation: CustomTranslationType.Master,
      title: "",
      text: "",
      resolution: "",
      isRight: false, // This property is not relevant for a sequential list, but we include it for consistency
      contentUrl: '',
      fillContainer: false,
      horizontalAlign: 'end',
      prompt: '',
      changePrompt: '',
      altText: '',
      imageLibrarySearchText: ''
    };

    this.element.options!.push(newOption);
  }

  onDeleteOption(index: number) {
    if (!this.element.options) return;
    this.element.options.splice(index, 1);
  }

  onOpenMdEditor(item: QuizOptionVm) {
    this.selectedOption = item;
    this.showMarkdownEditor = true;
  }

  onCopyToClipboard() {
    if (!this.element.options) return;
    let combinedText = this.element.title ? this.element.title + "\n\n" : "";
    combinedText += this.element.options.map((o, idx) => {
      const title = (o.title || '').toString().trim();
      const text = (o.text || '').toString().trim();
      const numberedTitle = title ? `${idx + 1}. ${title}` : '';
      if (numberedTitle && text) return `${numberedTitle}\n${text}`;
      return numberedTitle || text;
    }).filter(Boolean).join('\n\n');
    this.$globalHelper.copyToClipboard(combinedText, false);
    // this.$store.commit("ux/SB_SUCCESS", {
    //   message: "Text in Zwischenablage kopiert",
    //   timeout: 2000
    // });
  }

  async onOpenAiDialog(): Promise<void> {
    this.aiPrompt = "";
    this.showAiDialog = true;

    await this.$globalHelper.delay(20);
    const ref: any = this.$refs.aiPromptTextarea;
    if (!ref) return;
    // Try component focus first
    if (typeof ref.focus === 'function') {
      try { ref.focus(); return; } catch (e) {}
    }

    // Fallback to underlying textarea element
    const el = (ref.$el && ref.$el.querySelector) ? ref.$el.querySelector('textarea') : (ref.querySelector ? ref.querySelector('textarea') : null);
    if (el && typeof el.focus === 'function') el.focus();
  }

  onCloseAiDialog(): void {
    this.showAiDialog = false;
  }

  async onCreateContentList(): Promise<void> {
    this.isBusy = true;
    try {
      // Build known answers from existing options
      // const knownAnswers = (this.element.options ?? [])
      //   .filter(o => (o.text ?? '').trim() !== '')
      //   .map(o => o.text)
      //   .join(', ');

      const params: InteractiveListAiSuggestionVm = {
        page: this.selectedModulePage,
        title: this.element.title,
        contentPrompt: this.aiPrompt ?? "Keine weiteren Informationen.",
        elementList: this.element.options ?? []
      };

      const response: InteractiveListAiSuggestionVm = await rest.url("contentManager/suggestAiSequentialList").post(params);
      if (response) {
        this.element.title = response.title;

        // replace existing options with response
        this.element.options = [];
        response.elementList!.forEach((opt: any, idx: number) => {
          this.element.options!.push(<QuizOptionVm>{
            id: idx + 1,
            guid: this.$globalHelper.getNewGuid(),
            customTranslation: CustomTranslationType.Master,
            title: opt.title || (`Punkt ${idx + 1}`),
            text: opt.text,
            resolution: opt.resolution || '',
            isRight: false,
            selected: false,
            contentUrl: opt.contentUrl || '',
            fillContainer: opt.fillContainer !== undefined ? opt.fillContainer : false,
            horizontalAlign: opt.horizontalAlign !== undefined ? opt.horizontalAlign : 'end',
            prompt: opt.prompt || '',
            changePrompt: opt.changePrompt || '',
            altText: opt.altText || '',
            imageLibrarySearchText: opt.imageLibrarySearchText || ''
          });
        });

        // ensure sequential ids
        this.element.options.forEach((o: any, i: number) => o.id = i + 1);
        this.element.guid = this.$globalHelper.getNewGuid(); // since list has changed, invalidate old state by changing guid, so the user have to click all options again
        this.showAiDialog = false;
      }
    } catch (err) {
      console.error("Error creating AI content list:", err);
      // this.$store.commit("ux/SB_FAILURE", { message: "Fehler bei KI-Anfrage.", timeout: 5000 });
    } finally {
      this.isBusy = false;
    }
  }

  // get dropOptions() {
  //   return {
  //     accept: (data: any, ev?: DragEvent) => {
  //       // console.log("accept called: parsed:", data, "types:", ev?.dataTransfer?.types, "effectAllowed:", ev?.dataTransfer?.effectAllowed);
  //       if (!data) return false;
  //       if (data?.fileType && (data.fileType == "Markdown" || data.fileType == "Text")) return true;
  //       if (Array.isArray(data) && data[0] && data[0].name) return true;
  //       console.log("accept rejected");
  //       return false;
  //     },
  //     hoverClass: "drop-hover",
  //     transferTypes: ["Files"],
  //     targetGroups: ["markdown-file-details"],
  //     onDrop: (data: any) => {
  //       // console.log("Dropped data:", data);
  //       // Check for FileDetailsVm
  //       if (data?.fileType && (data.fileType == "Markdown" || data.fileType == "Text")) {
  //         this.element.text = data.aiSummary || "";
  //         this.element.contentUrl = data.filePath;
  //         return;
  //       }
  //       // Check for raw file array (dragged from OS / Browser). The file list is converted to array in the directive
  //       if (Array.isArray(data) && data[0] && data[0].name && (data[0].name.endsWith(".md") || data[0].name.endsWith(".txt"))) {
  //         // this.fileToUpload = data[0];
  //         const reader = new FileReader();
  //         reader.onload = (e: any) => {
  //           this.element.text = e.target.result;
  //           this.element.aiSummary = this.element.text;
  //           this.element.contentUrl = null;
  //         };
  //         reader.readAsText(data[0]);
  //       } else {
  //         this.$store.commit("ux/SB_FAILURE", {
  //           message: "Bei dieser Datei handelt es sich nicht um Text oder Markdown Datei.",
  //           timeout: 0
  //         });
  //       }
  //     }
  //   };
  // }
}
